import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  ArrowDownLeft, 
  ArrowUpRight, 
  Copy, 
  ExternalLink,
  ChevronDown,
  Wallet as WalletIcon
} from 'lucide-react'
import { useStore } from '@/store'
import { formatCurrency, formatAddress, copyToClipboard } from '@/lib/utils'
import { toast } from 'sonner'
import Button from '@/components/ui/Button'
import { Network } from '@/types'

const networkColors: Record<Network, string> = {
  base: 'from-blue-500 to-blue-600',
  ethereum: 'from-purple-500 to-purple-600',
  polygon: 'from-indigo-500 to-indigo-600',
  arbitrum: 'from-cyan-500 to-teal-500',
  optimism: 'from-red-500 to-orange-500',
}

const networkNames: Record<Network, string> = {
  base: 'Base',
  ethereum: 'Ethereum',
  polygon: 'Polygon',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
}

export default function Wallet() {
  const wallets = useStore(state => state.wallets)
  const [selectedNetwork, setSelectedNetwork] = useState<Network>('base')
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false)
  
  const selectedWallet = wallets.find(w => w.network === selectedNetwork) || wallets[0]
  
  const handleCopyAddress = async () => {
    const success = await copyToClipboard(selectedWallet.address)
    if (success) {
      toast.success('Address copied to clipboard')
    } else {
      toast.error('Failed to copy address')
    }
  }
  
  const handleDeposit = () => {
    toast.info('Deposit modal coming soon!')
  }
  
  const handleWithdraw = () => {
    toast.info('Withdraw modal coming soon!')
  }
  
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Wallet</h1>
        
        {/* Network Selector */}
        <div className="relative">
          <button
            onClick={() => setShowNetworkDropdown(!showNetworkDropdown)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-background-elevated hover:bg-background-hover transition-colors"
          >
            <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${networkColors[selectedNetwork]}`} />
            <span className="text-sm font-medium">{networkNames[selectedNetwork]}</span>
            <ChevronDown className="w-4 h-4 text-text-tertiary" />
          </button>
          
          {showNetworkDropdown && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute right-0 top-full mt-2 w-48 bg-background-card border border-border rounded-xl shadow-card overflow-hidden z-50"
            >
              {(Object.keys(networkNames) as Network[]).map((network) => (
                <button
                  key={network}
                  onClick={() => {
                    setSelectedNetwork(network)
                    setShowNetworkDropdown(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background-elevated transition-colors"
                >
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${networkColors[network]}`} />
                  <span className="text-sm">{networkNames[network]}</span>
                  {network === selectedNetwork && (
                    <div className="ml-auto w-2 h-2 rounded-full bg-brand" />
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </div>
      </div>
      
      {/* Balance Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-card border border-border p-6"
      >
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand/20 rounded-full blur-3xl" />
        
        <div className="relative z-10">
          <p className="text-text-secondary text-sm mb-2">Total Balance</p>
          <p className="text-balance text-text-primary">
            {formatCurrency(selectedWallet.balance.totalUsd)}
          </p>
          
          {/* Asset breakdown */}
          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl bg-background/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-500 font-bold text-xs">$</span>
                </div>
                <div>
                  <p className="font-medium">USDC</p>
                  <p className="text-xs text-text-tertiary">USD Coin</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatCurrency(selectedWallet.balance.usdc)}</p>
                <p className="text-xs text-text-tertiary">
                  {((selectedWallet.balance.usdc / selectedWallet.balance.totalUsd) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-xl bg-background/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <span className="text-teal-500 font-bold text-xs">T</span>
                </div>
                <div>
                  <p className="font-medium">USDT</p>
                  <p className="text-xs text-text-tertiary">Tether</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatCurrency(selectedWallet.balance.usdt)}</p>
                <p className="text-xs text-text-tertiary">
                  {((selectedWallet.balance.usdt / selectedWallet.balance.totalUsd) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-xl bg-background/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <span className="text-purple-500 font-bold text-xs">E</span>
                </div>
                <div>
                  <p className="font-medium">ETH</p>
                  <p className="text-xs text-text-tertiary">Ethereum</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">{selectedWallet.balance.eth.toFixed(4)} ETH</p>
                <p className="text-xs text-text-tertiary">
                  {((selectedWallet.balance.eth * 2400 / selectedWallet.balance.totalUsd) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="primary" onClick={handleDeposit}>
          <ArrowDownLeft className="w-5 h-5 mr-2" />
          Deposit
        </Button>
        <Button variant="secondary" onClick={handleWithdraw}>
          <ArrowUpRight className="w-5 h-5 mr-2" />
          Withdraw
        </Button>
      </div>
      
      {/* Wallet Address */}
      <div className="p-4 rounded-2xl bg-background-elevated border border-border">
        <p className="text-sm text-text-secondary mb-2">Wallet Address</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-sm font-mono text-text-primary truncate">
            {selectedWallet.address}
          </code>
          <button
            onClick={handleCopyAddress}
            className="p-2 rounded-lg hover:bg-background-hover transition-colors"
          >
            <Copy className="w-4 h-4 text-text-tertiary" />
          </button>
          <a
            href={`https://basescan.org/address/${selectedWallet.address}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg hover:bg-background-hover transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-text-tertiary" />
          </a>
        </div>
      </div>
      
      {/* All Wallets */}
      <section>
        <h2 className="text-lg font-semibold mb-4">All Wallets</h2>
        <div className="space-y-3">
          {wallets.map((wallet) => (
            <motion.button
              key={wallet.id}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => setSelectedNetwork(wallet.network)}
              className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${
                wallet.network === selectedNetwork
                  ? 'bg-brand/10 border-brand/50'
                  : 'bg-background-elevated border-border hover:border-border-hover'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${networkColors[wallet.network]} flex items-center justify-center`}>
                  <WalletIcon className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{networkNames[wallet.network]}</p>
                  <p className="text-sm text-text-tertiary">
                    {formatAddress(wallet.address)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatCurrency(wallet.balance.totalUsd)}</p>
                {wallet.isDefault && (
                  <span className="text-xs text-brand">Default</span>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </div>
  )
}